<template>
    <PageBanner
        title="Usage Metrics"
        subtext="View Te Kohinga usage metrics and trends over time."
    />
    <div class="metrics-page q-pa-md q-mr-md">

        <div class="row items-center q-mb-lg q-gutter-md">
            <q-select
                v-model="selectedKaimahi"
                :options="filteredKaimahiOptions"
                :option-label="kaimahiOptionLabel"
                option-value="email"
                label="Search kaimahi"
                clearable
                use-input
                hide-selected
                fill-input
                input-debounce="0"
                dense
                outlined
                bg-color="white"
                class="kaimahi-search"
                :loading="loadingKaimahiOptions"
                @filter="filterKaimahiOptions"
                @clear="clearKaimahiStats"
            >
                <template #option="scope">
                    <q-item v-bind="scope.itemProps">
                        <q-item-section>
                            <q-item-label>{{ kaimahiOptionLabel(scope.opt) }}</q-item-label>
                            <q-item-label caption>{{ scope.opt.email }}</q-item-label>
                        </q-item-section>
                    </q-item>
                </template>
                <template #no-option>
                    <q-item>
                        <q-item-section class="text-grey">No kaimahi found</q-item-section>
                    </q-item>
                </template>
            </q-select>
            <q-space />
            <template v-if="period === 'custom'">
                <q-input
                    v-model="customStart"
                    type="date"
                    dense
                    outlined
                    bg-color="white"
                    label="From"
                    style="width: 160px"
                    stack-label
                />
                <q-input
                    v-model="customEnd"
                    type="date"
                    dense
                    outlined
                    bg-color="white"
                    label="To"
                    style="width: 160px"
                    stack-label
                />
            </template>
            <q-select
                v-model="period"
                :options="periodOptions"
                emit-value
                map-options
                dense
                outlined
                bg-color="white"
                style="width: 160px"
            />
        </div>

        <q-card v-if="selectedKaimahi" class="q-mb-lg" flat bordered style="position: relative">
            <q-card-section>
                <div class="row items-start q-col-gutter-md q-mb-md">
                    <div class="col-12 col-md">
                        <div class="text-subtitle1">{{ selectedKaimahiLabel }}</div>
                        <div class="text-caption text-grey-7">{{ selectedKaimahi.email }}</div>
                    </div>
                    <div class="col-12 col-md-auto">
                        <q-btn
                            flat
                            dense
                            color="primary"
                            label="Clear"
                            @click="clearSelectedKaimahi"
                        />
                    </div>
                </div>

                <div class="row q-col-gutter-md q-mb-lg">
                    <div class="col-12 col-sm-6 col-md">
                        <div class="kaimahi-stat">
                            <div class="text-overline text-grey-8">Logins ({{ rangeLabel }})</div>
                            <div class="text-h5">{{ kaimahiStats?.loginStats?.totalLogins ?? '-' }}</div>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md">
                        <div class="kaimahi-stat">
                            <div class="text-overline text-grey-8">Forms Started ({{ rangeLabel }})</div>
                            <div class="text-h5">{{ kaimahiStats?.formStats?.overall?.total_started ?? '-' }}</div>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md">
                        <div class="kaimahi-stat">
                            <div class="text-overline text-grey-8">Forms Completed ({{ rangeLabel }})</div>
                            <div class="text-h5">{{ kaimahiStats?.formStats?.overall?.total_completed ?? '-' }}</div>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md">
                        <div class="kaimahi-stat">
                            <div class="text-overline text-grey-8">Active Clients</div>
                            <div class="text-h5">{{ kaimahiAssessmentSummary?.total_clients ?? '-' }}</div>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md">
                        <div class="kaimahi-stat">
                            <div class="text-overline text-grey-8">No Assessment Started</div>
                            <div class="text-h5">{{ kaimahiAssessmentSummary?.clients_without_assessment_started ?? '-' }}</div>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md">
                        <div class="kaimahi-stat">
                            <div class="text-overline text-grey-8">No Assessment Completed</div>
                            <div class="text-h5">{{ kaimahiAssessmentSummary?.clients_without_assessment_completed ?? '-' }}</div>
                        </div>
                    </div>
                </div>

                <div class="row q-col-gutter-md q-mb-lg">
                    <div class="col-12 col-md-6">
                        <div class="text-subtitle2 q-mb-sm">Kaimahi Login Activity - {{ rangeLabel }}</div>
                        <div v-if="kaimahiLoginChartData" style="position: relative; overflow: hidden">
                            <Bar :chart-data="kaimahiLoginChartData" :chart-options="barOptions" />
                        </div>
                        <div v-else-if="!loadingKaimahiStats" class="text-grey-5 text-center q-py-xl">No data</div>
                        <div v-else style="height: 200px" />
                    </div>
                    <div class="col-12 col-md-6">
                        <div class="text-subtitle2 q-mb-sm">Kaimahi Form Activity - {{ rangeLabel }}</div>
                        <div v-if="kaimahiFormChartData" style="position: relative; overflow: hidden">
                            <Bar :chart-data="kaimahiFormChartData" :chart-options="barOptions" />
                        </div>
                        <div v-else-if="!loadingKaimahiStats" class="text-grey-5 text-center q-py-xl">No data</div>
                        <div v-else style="height: 200px" />
                    </div>
                </div>

                <div class="row q-col-gutter-md">
                    <div class="col-12 col-md-6">
                        <div class="text-subtitle2 q-mb-sm">
                            TIK Assessment Status - {{ kaimahiAssessmentSummary?.total_clients ?? 0 }} active clients
                        </div>
                        <q-table
                            flat
                            dense
                            :rows="kaimahiAssessmentRows"
                            :columns="assessmentColumns"
                            row-key="status"
                            :pagination="{ rowsPerPage: 0 }"
                            hide-bottom
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <div class="text-subtitle2 q-mb-sm">Kaimahi Forms Breakdown - {{ rangeLabel }}</div>
                        <q-table
                            flat
                            dense
                            :rows="kaimahiStats?.formStats?.perForm ?? []"
                            :columns="formTableColumns"
                            row-key="form_id"
                            :loading="loadingKaimahiStats"
                        />
                    </div>
                </div>
            </q-card-section>
            <q-inner-loading :showing="loadingKaimahiStats"><q-spinner size="md" color="primary" /></q-inner-loading>
        </q-card>

        <div class="row q-gutter-md q-mb-lg">
            <q-card class="stat-card col-12 col-md" flat bordered style="position: relative">
                <q-card-section>
                    <div class="text-overline text-grey-8">Logins ({{ rangeLabel }})</div>
                    <div class="text-h4">{{ loginStats?.totalLogins ?? '—' }}</div>
                </q-card-section>
                <q-inner-loading :showing="loadingLogins"><q-spinner size="sm" color="primary" /></q-inner-loading>
            </q-card>
            <q-card class="stat-card col-12 col-md" flat bordered style="position: relative">
                <q-card-section>
                    <div class="text-overline text-grey-8">Unique Users ({{ rangeLabel }})</div>
                    <div class="text-h4">{{ loginStats?.uniqueUsers ?? '—' }}</div>
                </q-card-section>
                <q-inner-loading :showing="loadingLogins"><q-spinner size="sm" color="primary" /></q-inner-loading>
            </q-card>
            <q-card class="stat-card col-12 col-md" flat bordered style="position: relative">
                <q-card-section>
                    <div class="text-overline text-grey-8">Forms Started ({{ rangeLabel }})</div>
                    <div class="text-h4">{{ formStats?.overall?.total_started ?? '—' }}</div>
                </q-card-section>
                <q-inner-loading :showing="loadingForms"><q-spinner size="sm" color="primary" /></q-inner-loading>
            </q-card>
            <q-card class="stat-card col-12 col-md" flat bordered style="position: relative">
                <q-card-section>
                    <div class="text-overline text-grey-8">Forms Completed ({{ rangeLabel }})</div>
                    <div class="text-h4">{{ formStats?.overall?.total_completed ?? '—' }}</div>
                </q-card-section>
                <q-inner-loading :showing="loadingForms"><q-spinner size="sm" color="primary" /></q-inner-loading>
            </q-card>
        </div>

        <!-- Login & Form Charts -->
        <div class="row q-gutter-md q-mb-lg">
            <q-card class="col-12 col-md" flat bordered style="position: relative">
                <q-card-section>
                    <div class="text-subtitle1 q-mb-sm">Login Activity — {{ rangeLabel }}</div>
                    <div v-if="loginChartData" style="position: relative; overflow: hidden">
                        <Bar :chart-data="loginChartData" :chart-options="barOptions" />
                    </div>
                    <div v-else-if="!loadingLogins" class="text-grey-5 text-center q-py-xl">No data</div>
                    <div v-else style="height: 200px" />
                </q-card-section>
                <q-inner-loading :showing="loadingLogins"><q-spinner size="md" color="primary" /></q-inner-loading>
            </q-card>

            <q-card class="col-12 col-md" flat bordered style="position: relative">
                <q-card-section>
                    <div class="text-subtitle1 q-mb-sm">Form Activity — {{ rangeLabel }}</div>
                    <div v-if="formChartData" style="position: relative; overflow: hidden">
                        <Bar :chart-data="formChartData" :chart-options="barOptions" />
                    </div>
                    <div v-else-if="!loadingForms" class="text-grey-5 text-center q-py-xl">No data</div>
                    <div v-else style="height: 200px" />
                </q-card-section>
                <q-inner-loading :showing="loadingForms"><q-spinner size="md" color="primary" /></q-inner-loading>
            </q-card>
        </div>

        <!-- User & Form Tables -->
        <div class="row q-gutter-md">
            <q-card class="col-12 col-md" flat bordered>
                <q-card-section>
                    <div class="text-subtitle1 q-mb-sm">Top Users — {{ rangeLabel }}</div>
                    <q-table
                        flat
                        dense
                        :rows="loginStats?.topUsers ?? []"
                        :columns="topUsersColumns"
                        row-key="email"
                        :loading="loadingLogins"
                    />
                </q-card-section>
            </q-card>

            <q-card class="col-12 col-md" flat bordered>
                <q-card-section>
                    <div class="text-subtitle1 q-mb-sm">Forms Breakdown — {{ rangeLabel }}</div>
                    <q-table
                        flat
                        dense
                        :rows="formStats?.perForm ?? []"
                        :columns="formTableColumns"
                        row-key="form_id"
                        :loading="loadingForms"
                    />
                </q-card-section>
            </q-card>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import api from '../services/api/api';
import PageBanner from '../components/ClientComponents/DashboardComponents/PageBanner.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BAR_OPTIONS = markRaw({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } }
});

const PERIOD_OPTIONS = [
    { label: 'Last week',    value: 'week' },
    { label: 'Last month',   value: 'month' },
    { label: 'Last quarter', value: 'quarter' },
    { label: 'Last year',    value: 'year' },
    { label: 'All time',     value: 'all' },
    { label: 'Custom period', value: 'custom' },
];

const formatDate = (val) => {
    const d = new Date(val);
    return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
};

const toDateStr = (val) => String(val instanceof Date ? val.toISOString() : val).slice(0, 10);

const parseLocalDate = (dateStr) => {
    const [y, m, d] = toDateStr(dateStr).split('-').map(Number);
    return new Date(y, m - 1, d);
};

const granularityFor = (period, customStart, customEnd) => {
    if (period === 'custom' && customStart && customEnd) {
        const days = (new Date(customEnd) - new Date(customStart)) / 86400000;
        if (days < 14) return 'day';
        if (days < 90) return 'week';
        return 'month';
    }
    if (period === 'quarter') return 'week';
    if (period === 'year' || period === 'all') return 'month';
    return 'day';
};

const weekLabel = (firstStr, lastStr) => {
    const d1 = parseLocalDate(firstStr);
    const d2 = parseLocalDate(lastStr);
    const mon1 = d1.toLocaleDateString('en-NZ', { month: 'short' });
    const mon2 = d2.toLocaleDateString('en-NZ', { month: 'short' });
    if (mon1 === mon2) return `${d1.getDate()}-${d2.getDate()} ${mon1}`;
    return `${d1.getDate()} ${mon1} - ${d2.getDate()} ${mon2}`;
};

const monthLabel = (dateStr) => {
    return parseLocalDate(dateStr).toLocaleDateString('en-NZ', { month: 'short', year: 'numeric' });
};

/*
    Group daily rows into a given granularity (day/week/month) and sum the valueFields for each group. Returns { labels, groups } where:
    - labels is an array of label strings for each group (e.g. "1-7 Mar" for weekly groups, "Mar 2024" for monthly groups)
    - groups is an array of objects, one per group, where each object has the summed valueFields and no date field
*/
const groupDays = (rows, dateField, valueFields, granularity) => {
    if (granularity === 'day') {
        return { labels: rows.map(r => formatDate(r[dateField])), groups: rows };
    }

    const buckets = new Map();
    for (const row of rows) {
        const d = parseLocalDate(row[dateField]);
        let key;
        if (granularity === 'week') {
            const dow = d.getDay(); // 0=Sun
            const monday = new Date(d);
            monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
            key = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
        } else {
            key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        }
        if (!buckets.has(key)) buckets.set(key, { dateStrs: [], rows: [] });
        buckets.get(key).dateStrs.push(toDateStr(row[dateField]));
        buckets.get(key).rows.push(row);
    }

    const sorted = [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0]));

    const labels = sorted.map(([, { dateStrs }]) => {
        const s = dateStrs.slice().sort();
        return granularity === 'week' ? weekLabel(s[0], s[s.length - 1]) : monthLabel(s[0]);
    });

    const groups = sorted.map(([, { rows: br }]) => {
        const summed = {};
        for (const f of valueFields) summed[f] = br.reduce((acc, r) => acc + (Number(r[f]) || 0), 0);
        return summed;
    });

    return { labels, groups };
};

const periodToDateRange = (period) => {
    const end = new Date();
    let start;
    if (period === 'week')         { start = new Date(end); start.setDate(start.getDate() - 7); }
    else if (period === 'month')   { start = new Date(end); start.setMonth(start.getMonth() - 1); }
    else if (period === 'quarter') { start = new Date(end); start.setMonth(start.getMonth() - 3); }
    else if (period === 'year')    { start = new Date(end); start.setFullYear(start.getFullYear() - 1); }
    else                           { start = new Date(0); } // all time
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
};

export default {
    name: 'Metrics',
    components: { Bar, PageBanner },

    data() {
        return {
            period: 'month',
            periodOptions: PERIOD_OPTIONS,
            customStart: '',
            customEnd: '',
            loginStats: null,
            formStats: null,
            selectedKaimahi: null,
            kaimahiOptions: [],
            filteredKaimahiOptions: [],
            kaimahiStats: null,
            loadingLogins: false,
            loadingForms: false,
            loadingKaimahiOptions: false,
            loadingKaimahiStats: false,
            assessmentColumns: [
                { name: 'status', label: 'Status', field: 'status', align: 'left' },
                { name: 'clients', label: 'Clients', field: 'clients', align: 'right' },
            ],
            formTableColumns: [
                { name: 'form_title', label: 'Form', field: 'form_title', align: 'left' },
                { name: 'total_completed', label: 'Completed', field: 'total_completed', align: 'right' },
                { name: 'total_in_progress', label: 'In Progress', field: 'total_in_progress', align: 'right' },
                { name: 'total_started', label: 'Total', field: 'total_started', align: 'right' },
            ],
            topUsersColumns: [
                { name: 'full_name', label: 'Name', field: 'full_name', align: 'left' },
                { name: 'email', label: 'Email', field: 'email', align: 'left' },
                { name: 'login_count', label: 'Logins', field: 'login_count', align: 'right' },
            ],
            barOptions: BAR_OPTIONS
        };
    },

    watch: {
        period() { this.fetchData(); },
        customStart() { if (this.period === 'custom' && this.customStart && this.customEnd) this.fetchData(); },
        customEnd()   { if (this.period === 'custom' && this.customStart && this.customEnd) this.fetchData(); },
        selectedKaimahi() { this.fetchKaimahiStats(); },
    },

    computed: {
        rangeLabel() {
            if (this.period === 'custom') {
                if (this.customStart && this.customEnd)
                    return `${this.customStart} – ${this.customEnd}`;
                return 'Custom period';
            }
            return PERIOD_OPTIONS.find(o => o.value === this.period)?.label ?? '';
        },

        selectedKaimahiLabel() {
            return this.selectedKaimahi ? this.kaimahiOptionLabel(this.selectedKaimahi) : '';
        },

        kaimahiAssessmentSummary() {
            return this.kaimahiStats?.clientAssessmentStats?.summary ?? null;
        },

        kaimahiAssessmentRows() {
            const summary = this.kaimahiAssessmentSummary;
            if (!summary) return [];
            const started = summary.clients_with_assessment_started ?? 0;
            const completed = summary.clients_with_assessment_completed ?? 0;
            const notStarted = summary.clients_without_assessment_started ?? 0;
            return [
                { status: 'Not started', clients: notStarted },
                { status: 'In progress', clients: started - completed },
                { status: 'Completed', clients: completed },
            ];
        },

        loginChartData() {
            const days = this.loginStats?.byDay;
            if (!days?.length) return null;
            const { labels, groups } = groupDays(days, 'login_date', ['login_count'], granularityFor(this.period, this.customStart, this.customEnd));
            return {
                labels,
                datasets: [{
                    label: 'Logins',
                    data: groups.map(g => g.login_count),
                    backgroundColor: 'darkblue'
                }]
            };
        },

        formChartData() {
            const days = this.formStats?.recentActivity;
            if (!days?.length) return null;
            const { labels, groups } = groupDays(days, 'activity_date', ['forms_started', 'forms_completed'], granularityFor(this.period, this.customStart, this.customEnd));
            return {
                labels,
                datasets: [
                    { label: 'Started', data: groups.map(g => g.forms_started), backgroundColor: 'darkgreen' },
                    { label: 'Completed', data: groups.map(g => g.forms_completed), backgroundColor: 'darkblue' }
                ]
            };
        },

        kaimahiLoginChartData() {
            const days = this.kaimahiStats?.loginStats?.byDay;
            if (!days?.length) return null;
            const { labels, groups } = groupDays(days, 'login_date', ['login_count'], granularityFor(this.period, this.customStart, this.customEnd));
            return {
                labels,
                datasets: [{
                    label: 'Logins',
                    data: groups.map(g => g.login_count),
                    backgroundColor: 'darkblue'
                }]
            };
        },

        kaimahiFormChartData() {
            const days = this.kaimahiStats?.formStats?.recentActivity;
            if (!days?.length) return null;
            const { labels, groups } = groupDays(days, 'activity_date', ['forms_started', 'forms_completed'], granularityFor(this.period, this.customStart, this.customEnd));
            return {
                labels,
                datasets: [
                    { label: 'Started', data: groups.map(g => g.forms_started), backgroundColor: 'darkgreen' },
                    { label: 'Completed', data: groups.map(g => g.forms_completed), backgroundColor: 'darkblue' }
                ]
            };
        }
    },

    async created() {
        await Promise.all([this.fetchData(), this.fetchKaimahiOptions()]);
    },

    methods: {
        dateRangeParams() {
            return this.period === 'custom'
                ? { start: this.customStart, end: this.customEnd }
                : periodToDateRange(this.period);
        },

        async fetchData() {
            if (this.period === 'custom' && (!this.customStart || !this.customEnd)) return;
            this.loadingLogins = true;
            this.loadingForms = true;
            const params = this.dateRangeParams();
            const [loginResult, formResult] = await Promise.allSettled([
                api.metrics.getLoginStats(params),
                api.metrics.getFormStats(params)
            ]);
            if (loginResult.status === 'fulfilled') this.loginStats = loginResult.value;
            else console.error('[Metrics] login-stats failed:', loginResult.reason);
            if (formResult.status === 'fulfilled') this.formStats = formResult.value;
            else console.error('[Metrics] form-stats failed:', formResult.reason);
            this.loadingLogins = false;
            this.loadingForms = false;
            if (this.selectedKaimahi) await this.fetchKaimahiStats();
        },

        async fetchKaimahiOptions() {
            this.loadingKaimahiOptions = true;
            try {
                const response = await api.users.getAppStaffList();
                this.kaimahiOptions = response.data;
                this.filteredKaimahiOptions = response.data;
            } catch (error) {
                console.error('[Metrics] kaimahi options failed:', error);
            } finally {
                this.loadingKaimahiOptions = false;
            }
        },

        async fetchKaimahiStats() {
            if (!this.selectedKaimahi || (this.period === 'custom' && (!this.customStart || !this.customEnd))) {
                this.kaimahiStats = null;
                return;
            }

            this.loadingKaimahiStats = true;
            try {
                this.kaimahiStats = await api.metrics.getKaimahiStats({
                    ...this.dateRangeParams(),
                    staffEmail: this.selectedKaimahi.email
                });
            } catch (error) {
                console.error('[Metrics] kaimahi-stats failed:', error);
            } finally {
                this.loadingKaimahiStats = false;
            }
        },

        kaimahiOptionLabel(option) {
            if (!option) return '';
            const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
            return fullName || option.email;
        },

        filterKaimahiOptions(inputValue, update) {
            update(() => {
                const needle = String(inputValue).toLowerCase();
                this.filteredKaimahiOptions = this.kaimahiOptions.filter(option => {
                    const label = `${this.kaimahiOptionLabel(option)} ${option.email}`.toLowerCase();
                    return label.includes(needle);
                });
            });
        },

        clearSelectedKaimahi() {
            this.selectedKaimahi = null;
            this.clearKaimahiStats();
        },

        clearKaimahiStats() {
            this.kaimahiStats = null;
        }
    }
};
</script>

<style scoped>

.stat-card {
    border-radius: 12px;
}

.kaimahi-search {
    min-width: 320px;
    max-width: 460px;
    width: 100%;
}

.kaimahi-stat {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
    min-height: 86px;
    background: white;
}
</style>
